import {
  Document,
  Image,
  Page,
  StyleSheet,
  Text,
  View,
} from "@react-pdf/renderer";

import {
  PDF_COLORS,
  PDF_COMPANY,
  formatGeneratedAt,
  formatRp,
} from "@/lib/pdf";
import logo from "@/logo.png";

import { EXPENSE_PAYMENT_METHOD_LABELS } from "../expense-labels";
import { formatInvoiceDate, formatLongDate, formatPeriod } from "./format";

import type { Expense } from "@artisancode/api-types";

const COMPANY_NAME = PDF_COMPANY.name;
const COMPANY_ADDRESS = PDF_COMPANY.address;

const ORANGE = PDF_COLORS.orange;
const ORANGE_LIGHT = "#FBEAE0";
const DARK = PDF_COLORS.dark;
const GRAY = PDF_COLORS.gray;

const styles = StyleSheet.create({
  page: { padding: 36, fontSize: 9, fontFamily: "Helvetica", color: DARK },
  headerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  headerLeft: { flexShrink: 1 },
  docTitle: { fontFamily: "Helvetica-Bold", fontSize: 26, color: ORANGE },
  companyName: { fontFamily: "Helvetica-Bold", fontSize: 13, marginTop: 4 },
  subtitle: { fontSize: 9, color: GRAY, marginTop: 2 },
  logoBox: {
    width: 70,
    height: 70,
    alignItems: "center",
    justifyContent: "center",
  },
  logoImage: { width: 70, height: 70, objectFit: "contain" },
  divider: {
    borderBottomWidth: 2,
    borderBottomColor: ORANGE,
    marginTop: 16,
    marginBottom: 20,
  },
  infoRow: { flexDirection: "row", justifyContent: "space-between" },
  billedToTitle: {
    fontFamily: "Helvetica-Bold",
    fontSize: 13,
    color: ORANGE,
    marginBottom: 6,
  },
  billedToLine: { flexDirection: "row", marginTop: 2 },
  billedToLabel: { width: 90, color: GRAY },
  billedToValue: { flex: 1 },
  metaBlock: { alignItems: "flex-end" },
  metaLine: { flexDirection: "row", marginTop: 4 },
  metaLabel: { color: GRAY, marginRight: 12 },
  metaValue: { fontFamily: "Helvetica-Bold", fontSize: 10 },
  table: { marginTop: 24, borderWidth: 1, borderColor: "#eee" },
  tableHeaderRow: { flexDirection: "row", backgroundColor: ORANGE_LIGHT },
  tableHeaderCell: {
    fontFamily: "Helvetica-Bold",
    fontSize: 9,
    padding: 8,
    textAlign: "center",
  },
  tableRow: { flexDirection: "row", borderTopWidth: 1, borderTopColor: "#eee" },
  tableCell: { fontSize: 9, padding: 8, textAlign: "center" },
  tableCellLeft: { textAlign: "left" },
  tableCellRight: { textAlign: "right" },
  subtotalRow: {
    flexDirection: "row",
    backgroundColor: ORANGE_LIGHT,
    borderTopWidth: 1,
    borderTopColor: ORANGE,
  },
  footerRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginTop: 60,
  },
  footnote: { fontSize: 8, color: GRAY, maxWidth: "60%" },
  signatureBlock: { alignItems: "center" },
  signatureLine: { borderBottomWidth: 1, borderBottomColor: DARK, width: 160 },
  signatureName: { fontFamily: "Helvetica-Bold", fontSize: 10, marginTop: 4 },
  pageNumber: {
    position: "absolute",
    bottom: 20,
    right: 36,
    fontSize: 7,
    color: GRAY,
  },
  generatedAt: {
    position: "absolute",
    bottom: 20,
    left: 36,
    fontSize: 7,
    color: GRAY,
  },
});

export function ExpensePdfDocument({
  expenses,
  periodStart,
  periodEnd,
}: {
  expenses: Expense[];
  periodStart: string;
  periodEnd: string;
}) {
  const rows = [...expenses].sort((a, b) => a.date.localeCompare(b.date));
  const subtotal = rows.reduce(
    (sum, e) => sum + (e.subtotalAmount ?? e.amount),
    0,
  );
  const today = new Date().toISOString().slice(0, 10);
  const generatedAt = formatGeneratedAt();

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            <Text style={styles.docTitle}>INVOICE</Text>
            <Text style={styles.companyName}>{COMPANY_NAME}</Text>
            <Text style={styles.subtitle}>
              Dokumen transaksi pengeluaran operasional
            </Text>
          </View>
          <View style={styles.logoBox}>
            <Image src={logo} style={styles.logoImage} />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.infoRow}>
          <View style={{ width: "60%" }}>
            <Text style={styles.billedToTitle}>Billed To</Text>
            <View style={styles.billedToLine}>
              <Text style={styles.billedToLabel}>Customer&apos;s Name</Text>
              <Text style={styles.billedToValue}>: {COMPANY_NAME}</Text>
            </View>
            <View style={styles.billedToLine}>
              <Text style={styles.billedToLabel}>Address</Text>
              <Text style={styles.billedToValue}>: {COMPANY_ADDRESS}</Text>
            </View>
            <View style={styles.billedToLine}>
              <Text style={styles.billedToLabel}>Tax ID</Text>
              <Text style={styles.billedToValue}>: -</Text>
            </View>
          </View>

          <View style={styles.metaBlock}>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>Invoice Date</Text>
              <Text style={styles.metaValue}>{formatInvoiceDate(today)}</Text>
            </View>
            <View style={styles.metaLine}>
              <Text style={styles.metaLabel}>Periode</Text>
              <Text style={styles.metaValue}>
                {formatPeriod(periodStart, periodEnd)}
              </Text>
            </View>
          </View>
        </View>

        <View style={styles.table}>
          <View style={styles.tableHeaderRow}>
            <Text
              style={[
                styles.tableHeaderCell,
                { width: "28%", textAlign: "left" },
              ]}
            >
              Description
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "14%" }]}>Date</Text>
            <Text style={[styles.tableHeaderCell, { width: "16%" }]}>
              Unit Price
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "10%" }]}>Qty</Text>
            <Text style={[styles.tableHeaderCell, { width: "16%" }]}>
              Total
            </Text>
            <Text style={[styles.tableHeaderCell, { width: "16%" }]}>
              Payment
            </Text>
          </View>

          {rows.map((row) => (
            <View style={styles.tableRow} key={row.id} wrap={false}>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellLeft,
                  { width: "28%" },
                ]}
              >
                {row.description}
              </Text>
              <Text style={[styles.tableCell, { width: "14%" }]}>
                {formatInvoiceDate(row.date)}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellRight,
                  { width: "16%" },
                ]}
              >
                {formatRp(row.pricePerUnit)}
              </Text>
              <Text style={[styles.tableCell, { width: "10%" }]}>
                {row.qty}
              </Text>
              <Text
                style={[
                  styles.tableCell,
                  styles.tableCellRight,
                  { width: "16%" },
                ]}
              >
                {formatRp(row.subtotalAmount ?? row.amount)}
              </Text>
              <Text style={[styles.tableCell, { width: "16%" }]}>
                {EXPENSE_PAYMENT_METHOD_LABELS[row.paymentMethod]}
              </Text>
            </View>
          ))}

          <View style={styles.subtotalRow} wrap={false}>
            <Text style={[styles.tableCell, { width: "42%" }]} />
            <Text
              style={[
                styles.tableCell,
                {
                  width: "26%",
                  fontFamily: "Helvetica-Bold",
                  textAlign: "right",
                },
              ]}
            >
              SUBTOTAL
            </Text>
            <Text
              style={[
                styles.tableCell,
                styles.tableCellRight,
                { width: "16%", fontFamily: "Helvetica-Bold" },
              ]}
            >
              {formatRp(subtotal)}
            </Text>
            <Text style={[styles.tableCell, { width: "16%" }]} />
          </View>
        </View>

        <View style={styles.footerRow}>
          <Text style={styles.footnote}>
            Terima kasih. Invoice ini dibuat berdasarkan transaksi periode{" "}
            {formatLongDate(periodStart)} s/d {formatLongDate(periodEnd)}.
          </Text>
          <View style={styles.signatureBlock}>
            <View style={styles.signatureLine} />
            <Text style={styles.signatureName}>{COMPANY_NAME}</Text>
          </View>
        </View>

        <Text style={styles.generatedAt} fixed>
          Dicetak pada {generatedAt}
        </Text>
        <Text
          style={styles.pageNumber}
          render={({ pageNumber, totalPages }) =>
            `Halaman ${pageNumber}/${totalPages} - Pengeluaran ${formatPeriod(periodStart, periodEnd)}`
          }
          fixed
        />
      </Page>
    </Document>
  );
}
